import React, { useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { PackageV2Dto } from '@inosio/payments-sdk';
import { CheckRounded as CheckRoundedIcon } from '@mui/icons-material';

/**
 * Interface representing a single row in the transformed data.
 * Each row contains the feature, its category, and inclusion status for each package.
 */
interface TransformedFeatureRow {
  /**
   * The unique identifier for the feature or category.
   */
  feature: string;

  /**
   * The category to which the feature belongs.
   */
  category: string;

  /**
   * Dynamic keys representing inclusion status (`true`/`false`) for each package.
   * The key is the `PackageLabel` (e.g., "Basic", "Premium").
   */
  [packageName: string]: boolean | string;
}

export interface PackagesDetailLineItemsProps {
  Packages: PackageV2Dto[] | undefined;
}

/**
 * Transforms package data into a flat array suitable for rendering.
 * Consolidates features and categories across all packages and defaults missing `Included` values to `false`.
 *
 * @param {PackageV2Dto[] | undefined} packages - The package data to transform.
 * @returns {TransformedFeatureRow[]} - A flattened array of features and categories with inclusion status.
 */
const transformData = (packages: PackageV2Dto[] | undefined): TransformedFeatureRow[] => {
  const featureMap: Record<string, TransformedFeatureRow> = {};

  packages?.forEach(({ PackageLabel, PackageMetadata }) => {
    PackageMetadata?.DetailLineItems?.forEach((item) => {
      const category = item.Title;

      // Initialize the category row if it doesn't exist
      if (!featureMap[category]) {
        featureMap[category] = {
          feature: category,
          category: category,
        } as TransformedFeatureRow;
      }
      // Assign the inclusion status for the 'category'
      featureMap[category][PackageLabel] = item.Included || false;

      // Process each feature within the category
      item.Features.forEach((feature) => {
        const featureKey = `${category}: ${feature.Name}`;

        // Initialize the feature row if it doesn't exist
        if (!featureMap[featureKey]) {
          featureMap[featureKey] = {
            feature: featureKey,
            category: category,
          } as TransformedFeatureRow;
        }
        // Assign the inclusion status for the 'feature'
        featureMap[featureKey][PackageLabel] = feature.Included || false;
      });
    });
  });

  // Convert the hashmap to an array of rows
  return Object.values(featureMap);
};

const PackagesDetailLineItems = (props: PackagesDetailLineItemsProps) => {
  // Memoizing the transformed data so as to not transform it whenever the component rerenders
  const tableRows = useMemo(() => transformData(props.Packages), [props.Packages]);

  return (
    <TableContainer>
      <Typography sx={{ fontFamily: 'Work Sans !important', fontSize: 'large' }} style={{ padding: '16px' }}>
        Package Details:
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 800 }}>Services and Features</Typography>
            </TableCell>
            {props.Packages?.map((pkg) => (
              <TableCell key={pkg.PackageUuid} align="center">
                <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 800 }}>{pkg.PackageLabel}</Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map((row, index) => (
            <React.Fragment key={index}>
              {/* category row */}
              {row.feature === row.category && (
                <TableRow>
                  <TableCell sx={{ fontFamily: 'Work Sans !important', fontWeight: 800 }}>{row.category}</TableCell>
                  {props.Packages?.map((pkg) => (
                    <TableCell key={pkg.PackageUuid} align="center">
                      {row[pkg.PackageLabel] ? <CheckRoundedIcon /> : ''}
                    </TableCell>
                  ))}
                </TableRow>
              )}

              {/* feature rows */}
              {row.feature !== row.category && (
                <TableRow>
                  <TableCell sx={{ fontFamily: 'Work Sans !important', paddingLeft: '32px' }}>{row.feature.split(': ')[1]}</TableCell>
                  {props.Packages?.map((pkg) => (
                    <TableCell key={pkg.PackageUuid} align="center">
                      {row[pkg.PackageLabel] ? <CheckRoundedIcon /> : ''}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PackagesDetailLineItems;
